import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Save, ChevronDown, ChevronRight, Youtube, Trophy, Bookmark, Copy, TrendingUp } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { DAY_TYPE_LIST, DAY_TYPES, BLOCK_TYPE_LIST, BLOCK_TYPES } from "../lib/training";
import Sparkline from "./Sparkline";
import TeamLogo from "./TeamLogo";

const uid = () => Math.random().toString(36).slice(2, 11);

// Re-id blocks/exercises so a template can be applied without collisions
const cloneBlocks = (blocks = []) =>
  blocks.map((b) => ({
    ...b,
    id: uid(),
    exercises: (b.exercises || []).map((e) => ({ ...e, id: uid() })),
  }));

/**
 * SessionEditor - admin view to create/edit a session for a specific date.
 * Props: clientId, date (YYYY-MM-DD), session (existing or null), onClose, onSaved
 */
export default function SessionEditor({ clientId, date, session, onClose, onSaved }) {
  const isNew = !session;
  const [data, setData] = useState(
    session || {
      date,
      day_type: "fuerza",
      secondary_day_types: [],
      title: "",
      blocks: [],
      match: { rival: "", rival_logo_url: "", kickoff_time: "", location: "", notes: "" },
      prescribed_tests: [],
      notes: "",
    }
  );
  const [saving, setSaving] = useState(false);
  const [openBlock, setOpenBlock] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);

  const loadTemplates = async () => {
    try {
      const { data: list } = await api.get("/admin/session-templates");
      setTemplates(list);
    } catch { /* noop */ }
  };
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (tpl) => {
    setData((d) => ({
      ...d,
      day_type: tpl.day_type || d.day_type,
      title: tpl.title || d.title,
      blocks: cloneBlocks(tpl.blocks || []),
      notes: tpl.notes || d.notes,
    }));
    setShowTemplates(false);
    toast.success(`Plantilla "${tpl.name}" cargada`);
  };

  const removeTemplate = async (tid, e) => {
    e.stopPropagation();
    if (!confirm("¿Eliminar esta plantilla?")) return;
    try {
      await api.delete(`/admin/session-templates/${tid}`);
      setTemplates((arr) => arr.filter((t) => t.id !== tid));
      toast.success("Plantilla eliminada");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Nombre de la plantilla:", data.title || "Mi plantilla");
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try {
      const { data: created } = await api.post("/admin/session-templates", {
        name: name.trim(),
        day_type: data.day_type,
        title: data.title || null,
        blocks: data.blocks,
        notes: data.notes || null,
      });
      setTemplates((arr) => [created, ...arr]);
      toast.success("Plantilla guardada");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSavingTpl(false); }
  };

  useEffect(() => {
    if (session) {
      setData({
        ...session,
        secondary_day_types: session.secondary_day_types || [],
        match: session.match || { rival: "", rival_logo_url: "", kickoff_time: "", location: "", notes: "" },
        prescribed_tests: session.prescribed_tests || [],
      });
    }
  }, [session]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updateMatch = (k, v) => setData((d) => ({ ...d, match: { ...(d.match || {}), [k]: v } }));

  const toggleSecondary = (val) => {
    setData((d) => {
      const arr = d.secondary_day_types || [];
      if (val === d.day_type) return d; // can't duplicate primary
      return arr.includes(val)
        ? { ...d, secondary_day_types: arr.filter((x) => x !== val) }
        : { ...d, secondary_day_types: [...arr, val] };
    });
  };

  // Logo search state
  const [logoQuery, setLogoQuery] = useState("");
  const [logoResults, setLogoResults] = useState([]);
  const [logoSearching, setLogoSearching] = useState(false);
  const [logoNoResults, setLogoNoResults] = useState(false);

  const searchTeam = async (q) => {
    const query = (q ?? data.match?.rival ?? "").trim();
    if (!query) return;
    setLogoSearching(true); setLogoNoResults(false);
    try {
      const { data: res } = await api.get("/admin/search-team", { params: { q: query } });
      setLogoResults(res || []);
      setLogoNoResults((res || []).length === 0);
    } catch (err) {
      toast.error(formatErr(err));
      setLogoResults([]);
    } finally { setLogoSearching(false); }
  };

  const addBlock = () => {
    const blk = { id: uid(), type: "fuerza", name: "", exercises: [] };
    setData((d) => ({ ...d, blocks: [...d.blocks, blk] }));
    setOpenBlock(blk.id);
  };
  const removeBlock = (bid) => setData((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== bid) }));
  const updateBlock = (bid, fn) => setData((d) => ({ ...d, blocks: d.blocks.map((b) => (b.id === bid ? fn(b) : b)) }));
  const addEx = (bid) => updateBlock(bid, (b) => ({ ...b, exercises: [...b.exercises, { id: uid(), name: "", sets: "", reps: "", intensity: "", load: "", tempo: "", rest: "", notes: "", youtube_url: "" }] }));
  const removeEx = (bid, eid) => updateBlock(bid, (b) => ({ ...b, exercises: b.exercises.filter((e) => e.id !== eid) }));
  const updateEx = (bid, eid, k, v) => updateBlock(bid, (b) => ({ ...b, exercises: b.exercises.map((e) => (e.id === eid ? { ...e, [k]: v } : e)) }));
  const applyExerciseSuggestion = (bid, eid, sug) => updateBlock(bid, (b) => ({
    ...b,
    exercises: b.exercises.map((e) => (e.id === eid ? {
      ...e,
      name: sug.name || e.name,
      // Only overwrite empty fields so coach's adjustments are preserved
      youtube_url: e.youtube_url || sug.youtube_url || "",
      sets: e.sets || sug.sets || "",
      reps: e.reps || sug.reps || "",
      intensity: e.intensity || sug.intensity || "",
      load: e.load || sug.load || "",
      tempo: e.tempo || sug.tempo || "",
      rest: e.rest || sug.rest || "",
    } : e)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date: data.date,
        day_type: data.day_type,
        secondary_day_types: data.secondary_day_types || [],
        title: data.title || null,
        blocks: data.day_type === "tests" ? [] : data.blocks,
        match: data.day_type === "partido" || data.day_type === "partido_suplente" ? data.match : null,
        prescribed_tests: data.day_type === "tests" ? (data.prescribed_tests || []) : [],
        notes: data.notes || null,
      };
      if (isNew) {
        await api.post(`/admin/clients/${clientId}/sessions`, payload);
        toast.success("Sesión creada");
      } else {
        await api.put(`/admin/sessions/${session.id}`, payload);
        toast.success("Sesión actualizada");
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm("¿Eliminar esta sesión?")) return;
    try {
      await api.delete(`/admin/sessions/${session.id}`);
      toast.success("Eliminada");
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const dt = DAY_TYPES[data.day_type];
  const dateLabel = new Date(data.date + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="qv-modal-bg" onClick={onClose}>
      <div className="qv-modal" style={{ maxWidth: 820 }} onClick={(e) => e.stopPropagation()} data-testid="session-editor">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div>
            <div className="qv-overline">{isNew ? "Nueva sesión" : "Editar sesión"}</div>
            <h3 style={{ fontFamily: "Bebas Neue", fontSize: "1.8rem", textTransform: "capitalize" }}>{dateLabel}</h3>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer" }} data-testid="session-close"><X size={20} /></button>
        </div>

        {/* Templates toolbar */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", position: "relative" }}>
          <button
            type="button"
            onClick={() => { loadTemplates(); setShowTemplates((s) => !s); }}
            className="qv-btn-ghost"
            style={{ fontSize: 10 }}
            data-testid="load-template-btn"
          >
            <Copy size={12} /> CARGAR PLANTILLA {templates.length > 0 && <span style={{ color: "var(--qv-red)", marginLeft: 4 }}>({templates.length})</span>}
          </button>
          <button
            type="button"
            onClick={saveAsTemplate}
            disabled={savingTpl || data.blocks.length === 0}
            className="qv-btn-ghost"
            style={{ fontSize: 10, opacity: data.blocks.length === 0 ? 0.5 : 1 }}
            data-testid="save-template-btn"
            title={data.blocks.length === 0 ? "Añade bloques antes de guardar como plantilla" : ""}
          >
            <Bookmark size={12} /> {savingTpl ? "GUARDANDO..." : "GUARDAR COMO PLANTILLA"}
          </button>

          {showTemplates && (
            <div
              data-testid="templates-dropdown"
              style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 10,
                background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.12)",
                maxHeight: 280, overflowY: "auto",
              }}
            >
              {templates.length === 0 ? (
                <div style={{ padding: 16, fontSize: 12, color: "#737373", textAlign: "center" }}>
                  Aún no tienes plantillas guardadas. Crea una sesión y pulsa "Guardar como plantilla".
                </div>
              ) : (
                templates.map((tpl) => {
                  const dt2 = DAY_TYPES[tpl.day_type] || {};
                  const totalEx = (tpl.blocks || []).reduce((n, b) => n + (b.exercises || []).length, 0);
                  return (
                    <div
                      key={tpl.id}
                      onClick={() => applyTemplate(tpl)}
                      data-testid={`template-${tpl.id}`}
                      style={{
                        padding: "10px 14px",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#101010")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                        {dt2.color && <span style={{ width: 8, height: 8, background: dt2.color, flexShrink: 0 }} />}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: "#fff", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tpl.name}</div>
                          <div style={{ color: "#737373", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", marginTop: 2 }}>
                            {dt2.label || tpl.day_type} · {(tpl.blocks || []).length} bloques · {totalEx} ejercicios
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => removeTemplate(tpl.id, e)}
                        style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer", padding: 4, flexShrink: 0 }}
                        data-testid={`delete-template-${tpl.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Day type chips */}
        <div className="qv-overline" style={{ marginBottom: 8 }}>Tipo de día (principal)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 16 }}>
          {DAY_TYPE_LIST.map((t) => (
            <button
              key={t.value}
              onClick={() => update("day_type", t.value)}
              style={{
                background: data.day_type === t.value ? t.bg : "#070707",
                border: `1.5px solid ${data.day_type === t.value ? t.border : "rgba(255,255,255,0.08)"}`,
                color: data.day_type === t.value ? t.color : "#cfcfcf",
                padding: "10px 8px",
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                justifyContent: "center",
              }}
              data-testid={`daytype-${t.value}`}
            >
              <t.icon size={12} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Secondary activities */}
        <div className="qv-overline" style={{ marginBottom: 6 }}>Actividades extra (opcional)</div>
        <div style={{ fontSize: 10, color: "#737373", marginBottom: 10 }}>
          Marca las que se suman al día principal. Aparecerán como puntos extra en el calendario.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
          {DAY_TYPE_LIST.filter((t) => t.value !== data.day_type).map((t) => {
            const active = (data.secondary_day_types || []).includes(t.value);
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => toggleSecondary(t.value)}
                data-testid={`secondary-${t.value}`}
                style={{
                  background: active ? t.bg : "transparent",
                  border: `1px solid ${active ? t.border : "rgba(255,255,255,0.08)"}`,
                  color: active ? t.color : "#a3a3a3",
                  padding: "6px 10px",
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <t.icon size={11} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Match info */}
        {(data.day_type === "partido" || data.day_type === "partido_suplente") && (
          <div style={{ border: "1px solid rgba(225,29,46,0.3)", background: "rgba(225,29,46,0.05)", padding: 14, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--qv-red)", fontSize: 11, letterSpacing: "0.2em", fontWeight: 700, marginBottom: 12 }}>
              <Trophy size={14} /> DETALLES DEL PARTIDO {data.day_type === "partido_suplente" && <span style={{ color: "#F97316" }}>· SUPLENTE</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label>Rival</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    value={data.match.rival || ""}
                    onChange={(e) => { updateMatch("rival", e.target.value); setLogoQuery(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchTeam(); } }}
                    placeholder="Ej: FC Barcelona, Real Madrid…"
                    data-testid="match-rival"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => searchTeam()}
                    disabled={logoSearching || !(data.match.rival || "").trim()}
                    className="qv-btn-ghost"
                    style={{ fontSize: 10, whiteSpace: "nowrap" }}
                    data-testid="search-team-btn"
                  >
                    {logoSearching ? "BUSCANDO…" : "BUSCAR ESCUDO"}
                  </button>
                </div>
              </div>

              {/* Logo search results */}
              {(logoResults.length > 0 || logoNoResults) && (
                <div style={{ gridColumn: "1/-1", background: "#060606", border: "1px solid rgba(255,255,255,0.06)", padding: 10 }}>
                  {logoNoResults ? (
                    <div style={{ fontSize: 12, color: "#737373", textAlign: "center", padding: 8 }}>
                      Sin resultados. Pega la URL del escudo manualmente abajo.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                      {logoResults.map((r, i) => {
                        const selected = data.match.rival_logo_url === r.badge;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => { updateMatch("rival_logo_url", r.badge); updateMatch("rival", r.name); }}
                            data-testid={`team-result-${i}`}
                            style={{
                              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                              padding: 8,
                              background: selected ? "rgba(34,197,94,0.08)" : "#0a0a0a",
                              border: `1px solid ${selected ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.08)"}`,
                              cursor: "pointer",
                            }}
                          >
                            <TeamLogo src={r.badge} alt={r.name} size={44} bg="transparent" />
                            <div style={{ fontSize: 10, color: "#fff", fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}>{r.name}</div>
                            {r.league && <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.08em" }}>{r.league}</div>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="qv-field"><label>Hora</label><input value={data.match.kickoff_time || ""} onChange={(e) => updateMatch("kickoff_time", e.target.value)} placeholder="18:30" /></div>
              <div className="qv-field"><label>Lugar</label><input value={data.match.location || ""} onChange={(e) => updateMatch("location", e.target.value)} placeholder="Estadio" /></div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label>URL del escudo (manual, opcional)</label>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input value={data.match.rival_logo_url || ""} onChange={(e) => updateMatch("rival_logo_url", e.target.value)} placeholder="https://..." style={{ flex: 1 }} />
                  {data.match.rival_logo_url && (
                    <TeamLogo src={data.match.rival_logo_url} alt={data.match.rival || "preview"} size={34} bg="#0a0a0a" />
                  )}
                </div>
              </div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}><label>Notas del partido</label><textarea rows={2} value={data.match.notes || ""} onChange={(e) => updateMatch("notes", e.target.value)} placeholder="Plan de partido, calentamiento, etc." /></div>
            </div>
          </div>
        )}

        <div className="qv-field" style={{ marginBottom: 18 }}>
          <label>Título (opcional)</label>
          <input value={data.title || ""} onChange={(e) => update("title", e.target.value)} placeholder="Ej: Fuerza inferior" />
        </div>

        {/* Blocks (only for training day types) */}
        {data.day_type !== "tests" && (
        <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div className="qv-overline">Bloques</div>
          <button onClick={addBlock} className="qv-btn-ghost" style={{ fontSize: 10 }} data-testid="add-block">
            <Plus size={12} /> AÑADIR BLOQUE
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.blocks.map((b) => {
            const bt = BLOCK_TYPES[b.type] || BLOCK_TYPES.fuerza;
            const isOpen = openBlock === b.id;
            return (
              <div key={b.id} style={{ background: "#070707", border: `1px solid ${bt.color}30` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 12, cursor: "pointer" }} onClick={() => setOpenBlock(isOpen ? null : b.id)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {isOpen ? <ChevronDown size={14} color="#737373" /> : <ChevronRight size={14} color="#737373" />}
                    <bt.icon size={16} color={bt.color} />
                    <select value={b.type} onChange={(e) => updateBlock(b.id, (bl) => ({ ...bl, type: e.target.value }))} onClick={(e) => e.stopPropagation()} style={{ background: "transparent", border: "none", color: bt.color, fontWeight: 700, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer" }}>
                      {BLOCK_TYPE_LIST.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <span style={{ color: "#737373", fontSize: 12 }}>· {b.exercises.length} ejercicios</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer" }}><Trash2 size={14} /></button>
                </div>
                {isOpen && (
                  <div style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <input value={b.name || ""} onChange={(e) => updateBlock(b.id, (bl) => ({ ...bl, name: e.target.value }))} placeholder="Notas del bloque (opcional)" style={{ width: "100%", background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#cfcfcf", padding: "8px 10px", fontSize: 12, marginBottom: 10 }} />
                    {b.exercises.map((ex) => {
                      const cLog = (data.client_logs || {})[ex.id] || {};
                      const hasCLog = Object.values(cLog).some((v) => v && String(v).trim() !== "");
                      return (
                      <div key={ex.id} style={{ background: "#0c0c0c", border: "1px solid rgba(255,255,255,0.05)", padding: 10, marginBottom: 8 }}>
                        <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                          <ExerciseNameInput
                            value={ex.name}
                            onChange={(v) => updateEx(b.id, ex.id, "name", v)}
                            onPick={(sug) => applyExerciseSuggestion(b.id, ex.id, sug)}
                            testId={`ex-name-${ex.id}`}
                          />
                          <button onClick={() => removeEx(b.id, ex.id)} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer", padding: 4 }}><Trash2 size={14} /></button>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 6 }}>
                          {[
                            ["sets", "Series"],
                            ["reps", "Reps"],
                            ["intensity", "RPE/Int."],
                            ["load", "Carga"],
                            ["tempo", "Tempo"],
                            ["rest", "Descanso"],
                          ].map(([k, lbl]) => (
                            <input key={k} value={ex[k] || ""} onChange={(e) => updateEx(b.id, ex.id, k, e.target.value)} placeholder={lbl} style={{ background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 12 }} />
                          ))}
                          <input value={ex.youtube_url || ""} onChange={(e) => updateEx(b.id, ex.id, "youtube_url", e.target.value)} placeholder="YouTube URL" style={{ gridColumn: "1 / -1", background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 12 }} />
                        </div>

                        {/* Athlete log readonly preview */}
                        {hasCLog && (
                          <div style={{ marginTop: 10, padding: 10, background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.25)" }} data-testid={`coach-view-log-${ex.id}`}>
                            <div style={{ fontSize: 9, color: "#22C55E", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Registro del atleta</div>
                            {Array.isArray(cLog.sets_log) && cLog.sets_log.length > 0 ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                {cLog.sets_log.map((s, i) => (
                                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 11, color: "#cfcfcf", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                    <span style={{ color: "#22C55E", fontSize: 9, fontWeight: 700, minWidth: 52 }}>SERIE {i + 1}</span>
                                    <span>{[s.reps && `${s.reps} reps`, s.load, s.intensity, s.rest && `Desc. ${s.rest}`].filter(Boolean).join(" · ") || "—"}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 11, color: "#cfcfcf", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                {cLog.sets && <span>{cLog.sets} series</span>}
                                {cLog.reps && <span>{cLog.reps} reps</span>}
                                {cLog.load && <span>{cLog.load}</span>}
                                {cLog.intensity && <span>{cLog.intensity}</span>}
                                {cLog.rest && <span>Desc. {cLog.rest}</span>}
                              </div>
                            )}
                            {cLog.notes && <div style={{ fontSize: 12, color: "#cfcfcf", marginTop: 6, fontStyle: "italic" }}>"{cLog.notes}"</div>}
                          </div>
                        )}

                        {/* Progression chart for coach */}
                        {clientId && ex.name && (
                          <CoachExerciseProgression clientId={clientId} exerciseName={ex.name} />
                        )}
                      </div>
                    );})}
                    <button onClick={() => addEx(b.id)} className="qv-btn-ghost" style={{ fontSize: 10 }}>
                      <Plus size={12} /> AÑADIR EJERCICIO
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {data.blocks.length === 0 && (
            <div className="qv-empty" style={{ padding: 24 }}>Aún no hay bloques. Pulsa "Añadir bloque".</div>
          )}
        </div>
        </>
        )}

        {/* Tests propuestos (only when day_type=tests) */}
        {data.day_type === "tests" && (
          <PrescribedTestsSection
            tests={data.prescribed_tests || []}
            onChange={(arr) => update("prescribed_tests", arr)}
          />
        )}

        <div className="qv-field" style={{ marginTop: 18 }}>
          <label>Notas generales</label>
          <textarea rows={2} value={data.notes || ""} onChange={(e) => update("notes", e.target.value)} />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
          {!isNew && (
            <button onClick={remove} className="qv-btn-ghost" style={{ color: "var(--qv-red)", borderColor: "rgba(225,29,46,0.4)" }} data-testid="session-delete">
              <Trash2 size={14} /> ELIMINAR
            </button>
          )}
          <button onClick={save} disabled={saving} className="qv-btn-primary" style={{ marginLeft: "auto" }} data-testid="session-save">
            <Save size={14} /> {saving ? "GUARDANDO..." : "GUARDAR SESIÓN"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CoachExerciseProgression({ clientId, exerciseName }) {  const [data, setData] = useState(null);
  useEffect(() => {
    let cancel = false;
    if (!exerciseName) return;
    (async () => {
      try {
        const { data: prog } = await api.get(`/admin/clients/${clientId}/exercises/progression`, {
          params: { name: exerciseName, limit: 8 },
        });
        if (!cancel) setData(prog || []);
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [clientId, exerciseName]);

  if (!data || data.length < 2) return null;
  const values = data.map((p) => p.avg_load);
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  const delta = +(last.avg_load - prev.avg_load).toFixed(1);
  const improved = delta >= 0;
  return (
    <div style={{ marginTop: 10, padding: 10, background: "rgba(245,197,24,0.04)", border: "1px solid rgba(245,197,24,0.18)" }} data-testid={`coach-progression-${exerciseName}`}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 9, color: "#EAB308", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700 }}>
          <TrendingUp size={11} /> Progresión carga · {data.length} sesiones
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10 }}>
          <span style={{ color: "#737373" }}>Última</span>
          <span style={{ fontFamily: "Bebas Neue", fontSize: "1.05rem", color: "#EAB308", lineHeight: 1 }}>{last.avg_load}</span>
          <span style={{ color: improved ? "#22C55E" : "#E11D2E", fontWeight: 700 }}>
            {delta > 0 ? "+" : ""}{delta}
          </span>
        </div>
      </div>
      <Sparkline values={values} color="#EAB308" height={36} />
    </div>
  );
}


const EMPTY_TEST_TYPES = [
  { value: "vertical_jump", label: "Salto vertical", unit: "cm" },
  { value: "sprint_10m", label: "Sprint 10m", unit: "s" },
  { value: "sprint_30m", label: "Sprint 30m", unit: "s" },
  { value: "squat_1rm", label: "Squat 1RM", unit: "kg" },
  { value: "bench_1rm", label: "Press banca 1RM", unit: "kg" },
  { value: "weight", label: "Peso corporal", unit: "kg" },
  { value: "body_fat", label: "% Grasa", unit: "%" },
  { value: "custom", label: "Personalizado", unit: "" },
];

function PrescribedTestsSection({ tests, onChange }) {
  const [templates, setTemplates] = useState([]);
  const [showTpls, setShowTpls] = useState(false);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/admin/test-templates");
      setTemplates(data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadTemplates(); }, []);

  const addBlank = () => {
    onChange([...tests, { id: uid(), test_type: "vertical_jump", label: "", unit: "cm", description: "", tutorial_url: "", target: "" }]);
  };
  const addFromTemplate = (tpl) => {
    onChange([...tests, {
      id: uid(),
      test_type: tpl.test_type,
      label: tpl.label || "",
      unit: tpl.unit || "",
      description: tpl.description || "",
      tutorial_url: tpl.tutorial_url || "",
      target: tpl.target || "",
    }]);
    setShowTpls(false);
    toast.success(`Test "${tpl.name}" añadido`);
  };
  const updateAt = (i, k, v) => onChange(tests.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const removeAt = (i) => onChange(tests.filter((_, idx) => idx !== i));
  const removeTemplate = async (tid, e) => {
    e.stopPropagation();
    if (!confirm("¿Eliminar esta plantilla?")) return;
    try {
      await api.delete(`/admin/test-templates/${tid}`);
      setTemplates((arr) => arr.filter((t) => t.id !== tid));
      toast.success("Plantilla eliminada");
    } catch (err) { toast.error(formatErr(err)); }
  };
  const saveAsTemplate = async (i) => {
    const t = tests[i];
    const defaultName = EMPTY_TEST_TYPES.find((e) => e.value === t.test_type)?.label || t.label || "Mi test";
    const name = window.prompt("Nombre de la plantilla:", defaultName);
    if (!name || !name.trim()) return;
    try {
      const { data: created } = await api.post("/admin/test-templates", {
        name: name.trim(),
        test_type: t.test_type,
        label: t.label || null,
        unit: t.unit || "",
        description: t.description || null,
        tutorial_url: t.tutorial_url || null,
        target: t.target || null,
      });
      setTemplates((arr) => [created, ...arr]);
      toast.success("Plantilla guardada");
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="prescribed-tests">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div className="qv-overline">Tests propuestos</div>
        <div style={{ display: "flex", gap: 6, position: "relative" }}>
          <button
            type="button"
            onClick={() => { loadTemplates(); setShowTpls((s) => !s); }}
            className="qv-btn-ghost"
            style={{ fontSize: 10 }}
            data-testid="load-test-template"
          >
            <Copy size={12} /> CARGAR PLANTILLA {templates.length > 0 && <span style={{ color: "#EAB308", marginLeft: 4 }}>({templates.length})</span>}
          </button>
          <button type="button" onClick={addBlank} className="qv-btn-ghost" style={{ fontSize: 10 }} data-testid="add-test">
            <Plus size={12} /> NUEVO TEST
          </button>

          {showTpls && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 10, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.12)", maxHeight: 280, overflowY: "auto", minWidth: 260 }} data-testid="test-templates-dropdown">
              {templates.length === 0 ? (
                <div style={{ padding: 16, fontSize: 12, color: "#737373", textAlign: "center" }}>
                  Aún no tienes plantillas. Crea un test y pulsa "Guardar como plantilla".
                </div>
              ) : (
                templates.map((tpl) => (
                  <div
                    key={tpl.id}
                    onClick={() => addFromTemplate(tpl)}
                    data-testid={`test-template-${tpl.id}`}
                    style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.05)", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#101010")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{tpl.name}</div>
                      <div style={{ color: "#737373", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                        {tpl.test_type}{tpl.unit ? ` · ${tpl.unit}` : ""}{tpl.description ? " · con instrucciones" : ""}
                      </div>
                    </div>
                    <button onClick={(e) => removeTemplate(tpl.id, e)} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer", padding: 4 }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {tests.length === 0 ? (
        <div className="qv-empty" style={{ padding: 24 }}>Aún no hay tests. Pulsa "Cargar plantilla" o "Nuevo test".</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {tests.map((t, i) => (
            <div key={t.id} style={{ background: "#070707", border: "1px solid rgba(234,179,8,0.18)", padding: 12 }} data-testid={`prescribed-test-${t.id}`}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <select value={t.test_type} onChange={(e) => {
                  const tt = EMPTY_TEST_TYPES.find((x) => x.value === e.target.value);
                  updateAt(i, "test_type", e.target.value);
                  if (tt && (!t.unit || EMPTY_TEST_TYPES.some((x) => x.unit === t.unit))) updateAt(i, "unit", tt.unit);
                }} style={{ background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 12, flex: "0 1 180px" }}>
                  {EMPTY_TEST_TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                </select>
                {t.test_type === "custom" && (
                  <input value={t.label || ""} onChange={(e) => updateAt(i, "label", e.target.value)} placeholder="Nombre del test" style={{ background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 12, flex: "1 1 200px" }} />
                )}
                <input value={t.unit || ""} onChange={(e) => updateAt(i, "unit", e.target.value)} placeholder="Unidad" style={{ background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 12, width: 80 }} />
                <input value={t.target || ""} onChange={(e) => updateAt(i, "target", e.target.value)} placeholder="Objetivo" style={{ background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 12, width: 100 }} />
                <button onClick={() => saveAsTemplate(i)} className="qv-btn-ghost" style={{ fontSize: 10 }} data-testid={`save-test-tpl-${t.id}`}>
                  <Bookmark size={11} /> GUARDAR PLANTILLA
                </button>
                <button onClick={() => removeAt(i)} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: "#737373", cursor: "pointer", padding: "6px 8px" }}>
                  <Trash2 size={12} />
                </button>
              </div>
              <textarea
                rows={3}
                value={t.description || ""}
                onChange={(e) => updateAt(i, "description", e.target.value)}
                placeholder="Instrucciones paso a paso..."
                style={{ width: "100%", background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "8px 10px", fontSize: 12, marginBottom: 8 }}
              />
              <input
                value={t.tutorial_url || ""}
                onChange={(e) => updateAt(i, "tutorial_url", e.target.value)}
                placeholder="URL vídeo demostración (YouTube/Drive/etc.)"
                style={{ width: "100%", background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 12 }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ExerciseNameInput({ value, onChange, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    const q = (value || "").trim();
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/exercise-suggestions", { params: { q, limit: 8 } });
        setSuggestions(data || []);
      } catch { setSuggestions([]); }
    }, 180);
    return () => clearTimeout(handle);
  }, [value, open]);

  const pick = (sug) => {
    onPick(sug);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative", flex: 1 }}>
      <input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="Ejercicio (escribe para ver sugerencias)"
        data-testid={testId}
        style={{ width: "100%", background: "#060606", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "8px 10px", fontSize: 13, fontWeight: 700 }}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.12)", zIndex: 50, maxHeight: 220, overflowY: "auto" }}
        >
          {suggestions.map((s, i) => (
            <div
              key={i}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#101010")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: "#fff", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                <div style={{ color: "#737373", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 2 }}>
                  {[s.sets && `${s.sets}×${s.reps || "?"}`, s.load, s.intensity, s.youtube_url && "vídeo"].filter(Boolean).join(" · ") || "Sin pauta guardada"}
                </div>
              </div>
              <span style={{ color: "var(--qv-red)", fontSize: 10, letterSpacing: "0.18em", fontWeight: 700, flexShrink: 0 }}>×{s.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
